import React, { useContext, useEffect, useMemo, useState } from 'react';
import { useStyles } from '../../../LawPortal.styles';

import { useAptorApi } from '../../../../../Api';
import { Module, UserContext } from '../../../../../Context/UserContext/UserContext';
import { ModuleContext } from '../../../../../Context/ModuleContext/ModuleContext';
import { useIntl } from 'react-intl';
import { ArrowBack } from '@material-ui/icons';
import { ButtonLink, HtmlView, Panel } from '../../../../../Components';
import { useNavigate, useLocation } from 'react-router-dom';
import { Box, Button, Paper, Grid, Typography } from '@material-ui/core';
import start from '../../../../../Assets/Images/start.png';
import { CardItem } from '../../../../../Components/Home/Components/Card/Card';
import { CreatePlanWizard, loadWizardSession, clearWizardSession, WizardSession } from '../CreatePlanWizard';

type PlanType = 'one-time' | 'recurring' | 'from-completed' | 'create' | 'start';

const PRIMARY = '#c4122f';

export const PlanningLK = React.memo(() => {
  const classes = useStyles();
  const { formatMessage } = useIntl();
  const { api, abortController } = useAptorApi();
  const { hasAccessToModule, customerId, id: userId } = useContext(UserContext);
  const { setHeader } = useContext(ModuleContext);

  const navigate = useNavigate();
  const location = useLocation();

  const [wizardOpen, setWizardOpen] = useState(false);
  const [planType, setPlanType] = useState<PlanType>('start');
  const [editPlanData, setEditPlanData] = useState<any>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [evalutionInfo, setEvalutionInfo] = useState<any>(null);
  const [savedSession, setSavedSession] = useState<WizardSession | null | undefined>(undefined);
  const [resumeSession, setResumeSession] = useState<WizardSession | null>(null);

  useEffect(() => {
    api
      .getevaluationSettings(customerId)
      .then((result) => {
        if (abortController.current.signal.aborted) return;
        setEvalutionInfo(result);
      })
      .catch((err) => {
        console.error('API error:', err);
        setEvalutionInfo(undefined);
      });
  }, [customerId]);

  // ── On mount: check for edit navigation OR existing saved session ──────────
  useEffect(() => {
    const state = location.state as any;

    if (state?.editPlan) {
      setEditPlanData(state.editPlan);
      setIsEditMode(true);
      setPlanType('recurring');
      setResumeSession(null);
      setSavedSession(null);
      setWizardOpen(true);
      navigate(location.pathname, { replace: true, state: {} });
      return;
    }

    const session = loadWizardSession(userId);
    setSavedSession(session);
  }, []); // run once on mount only

  const menuConfig = useMemo(() => {
    const hasAdminAccess =
      hasAccessToModule(Module.Administration) || hasAccessToModule(Module.Organization);

    const baseMenu = [
      {
        title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.carry.lek' }),
        to: `/law-portal/legal-compliance-control/carry`,
      },
      {
        title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.lek' }),
        to: `/law-portal/legal-compliance-control/reports`,
      },
      {
        title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.reports.observations' }),
        to: `/law-portal/legal-compliance-control/observations`,
      },
    ];

    if (hasAdminAccess) {
      return [
        {
          title: formatMessage({ id: 'law-portal.legal.compliance.sub-menu.planning.lek' }),
          to: `/law-portal/legal-compliance-control/planning`,
        },
        ...baseMenu,
      ];
    }

    return baseMenu;
  }, [hasAccessToModule, formatMessage]);

  useEffect(() => {
    setHeader(
      formatMessage({ id: 'law-portal.legal.compliance.header', defaultMessage: 'Evaluation control' }),
      menuConfig,
    );
  }, [setHeader, formatMessage, menuConfig]);

  // ── Handlers ──────────────────────────────────────────────────────────────

  // FIX: handleStartClick no longer calls clearWizardSession or handleDiscardAndStart.
  // Session is preserved until user explicitly discards via ResumeBanner "Discard" button,
  // or cancels/finishes inside the wizard.
  const handleStartClick = () => {
    setEditPlanData(null);
    setIsEditMode(false);
    setResumeSession(null);
    setPlanType('start');
    setWizardOpen(true);
  };

  const handleResume = () => {
    if (!savedSession) return;
    setIsEditMode(false);
    setPlanType(savedSession.planType as PlanType);
    setResumeSession(savedSession);
    setSavedSession(null);
    setWizardOpen(true);
  };

  const handleDiscardAndStart = () => {
    clearWizardSession(userId);
    setSavedSession(null);
    setResumeSession(null);
    setEditPlanData(null);
    setIsEditMode(false);
    setPlanType('start');
    setWizardOpen(true);
  };

  const handleWizardClose = () => {
    setWizardOpen(false);
    setEditPlanData(null);
    setResumeSession(null);
    const wasEdit = isEditMode;
    setIsEditMode(false);
    if (!wasEdit) {
      const session = loadWizardSession(userId);
      setSavedSession(session);
    }
  };

  const handleWizardback = () => {
    setWizardOpen(false);
    setEditPlanData(null);
    setResumeSession(null);
    setIsEditMode(false);
    // Always reload session — edit ho ya na ho
    const session = loadWizardSession(userId);
    setSavedSession(session);
  };

  const handlePlanCreated = () => {
    navigate('/law-portal/legal-compliance-control/carry');
  };

  // ── Render ────────────────────────────────────────────────────────────────
  if (savedSession === undefined) return null;

  return (
    <div className={classes.listWrapper}>
      {wizardOpen && (
        <ButtonLink
          text={formatMessage({ id: 'component.back-button.back' })}
          onclick={() => handleWizardback()}
          to={''}
          icon={<ArrowBack />}
        />
      )}
      <hr style={{ marginTop: '25px', marginBottom: '25px' }} />

      {wizardOpen ? (
        <div className={classes.listWrapper}>
          <CreatePlanWizard
            open={wizardOpen}
            planType={planType}
            onClose={handleWizardClose}
            getCompliancePlanList={handlePlanCreated}
            editPlan={editPlanData}
            resumeSession={resumeSession}
          />
        </div>
      ) : (
        <>
          {/* ── Resume banner ── */}
          {/* {savedSession && (
            <ResumeBanner
              session={savedSession}
              onResume={handleResume}
              onDiscard={() => { clearWizardSession(userId); setSavedSession(null); }}
              formatMessage={formatMessage}
            />
          )} */}

          {/* ── Single start card ── */}
          <Grid container spacing={2} style={{ marginTop: '40px' }} alignItems="stretch">

            {/* FIX: CardItem always calls handleStartClick.
                Previously: savedSession ? handleDiscardAndStart : handleStartClick
                That was silently wiping the session before user could see the Resume banner.
                Now only the ResumeBanner "Discard" button can explicitly delete a session. */}
            <CardItem
              label="law-portal.legal.compliance.sub-menu.planning.btn.start-evealution"
              image={start}
              onClick={handleStartClick}
            />

            <Grid item xs style={{ display: 'flex' }}>
              <Panel
                title={formatMessage({ id: 'law-portal.legal.compliance.header' })}
                style={{ width: '100%', height: '100%', boxSizing: 'border-box' }}
              >
                <Typography variant="body1">
                  {/* <HtmlView value={evalutionInfo} /> */}
                  Lorem Ipsum is simply dummy text of the printing and typesetting industry. Lorem Ipsum has been the industry's standard dummy text ever since the 1500s, when an unknown printer took a galley of type and scrambled it to make a type specimen book. It has survived not only five centuries, but also the leap into electronic typesetting, remaining essentially unchanged.
                </Typography>
              </Panel>
            </Grid>

          </Grid>
        </>
      )}
    </div>
  );
});

PlanningLK.displayName = 'PlanningLK';

export default PlanningLK;

// ─────────────────────────────────────────────
// Resume Banner component
// ─────────────────────────────────────────────
const PLAN_TYPE_LABELS: Record<string, string> = {
  'one-time': 'law-portal.legal.compliance.type.planning.one.time',
  'recurring': 'law-portal.legal.compliance.type.planning.recurring.plan',
  'from-completed': 'law-portal.legal.compliance.type.planning.earlier.completed.plan',
};

const ResumeBanner = ({
  session,
  onResume,
  onDiscard,
  formatMessage,
}: {
  session: WizardSession;
  onResume: () => void;
  onDiscard: () => void;
  formatMessage: any;
}) => {
  // Determine the effective type to show in the banner:
  // 1. If planType is 'start' or 'create', check _resolvedFlow first:
  //    - If _resolvedFlow === 'from-completed' → show 'from-completed'
  //    - Otherwise fall back to _resolvedPlanType (one-time / recurring)
  // 2. For direct planTypes (one-time, recurring, from-completed), use as-is
  const effectiveType = (() => {
    if (session.planType === 'start' || session.planType === 'create') {
      if (session.stepData?._resolvedFlow === 'from-completed') return 'from-completed';
      return session.stepData?._resolvedPlanType;
    }
    return session.planType;
  })();
  const planLabel = effectiveType ? PLAN_TYPE_LABELS[effectiveType as string] : undefined;
  const stepNumber = session.currentStep;

  return (
    <Paper
      variant="outlined"
      style={{
        padding: '16px 24px',
        marginBottom: '24px',
        marginTop: '24px',
        borderColor: PRIMARY,
        borderWidth: '1.5px',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'space-between',
        flexWrap: 'wrap',
        gap: '12px',
      }}
    >
      <Box>
        <Typography variant="subtitle1" style={{ fontWeight: 700, color: PRIMARY }}>
          {formatMessage({ id: 'law-portal.evalution.plan.unfinished.plan.text' })}
        </Typography>
        <Typography variant="body2" style={{ color: '#555', marginTop: '4px' }}>
          {planLabel && (
            <>
              <strong>{formatMessage({ id: 'admin.sendouts.table.type' })}:</strong>{' '}
              {formatMessage({ id: planLabel })}
              &nbsp;·&nbsp;
            </>
          )}
          <strong>{formatMessage({ id: 'law-portal.evalution.plan.text.step' })}:</strong> {stepNumber}
        </Typography>
      </Box>
      <Box style={{ display: 'flex', gap: '10px' }}>
        <Button
          variant="contained"
          onClick={onResume}
          style={{ background: PRIMARY, color: '#fff', fontWeight: 700, textTransform: 'none' }}
        >
          {formatMessage({ id: 'law-portal.evalution.plan.btn.resume', defaultMessage: 'Resume' })}
        </Button>
        <Button
          variant="outlined"
          onClick={onDiscard}
          style={{ borderColor: PRIMARY, color: PRIMARY, fontWeight: 700, textTransform: 'none' }}
        >
          {formatMessage({ id: 'law-portal.evalution.plan.btn.discard', defaultMessage: 'Discard' })}
        </Button>
      </Box>
    </Paper>
  );
};